import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { CardGrid } from '../../components/CardGrid';
import { ErrorText } from '../../components/ErrorText';
import { ItemCard } from '../../components/ItemCard';
import { Input } from '../../components/Input';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { Spinner } from '../../components/Spinner';
import { TextButton } from '../../components/TextButton';
import { BookingSummaryRow } from '../../composites/BookingSummaryRow';
import {
  BookingColumn,
  BookingList,
  Columns,
  ConfirmationMeta,
  CreateAccountErrorAction,
  CreateAccountForm,
  CreateAccountSuccess,
  Header,
  ManageBooking,
  ManageBookingDescription,
  SectionTitle,
  UpsellGridWrapper,
  UpsellSection,
  VoucherItem,
  VoucherList,
} from './ConfirmationTemplate.styles';
import type { ConfirmationTemplateProps } from './ConfirmationTemplate.types';

export function ConfirmationTemplate({
  heading,
  subheading,
  confirmationEmail,
  confirmationNumber,
  summaryLines = [],
  voucherCodes = [],
  bookingItems,
  createAccount,
  upsell,
  labels,
}: ConfirmationTemplateProps) {
  const [email, setEmail] = useState(createAccount?.email ?? '');
  const [isEmailEditedSinceSubmit, setIsEmailEditedSinceSubmit] = useState(false);
  const createAccountErrorId = useId();
  const successRef = useRef<HTMLParagraphElement>(null);

  const successMessage = createAccount?.successMessage;
  const isSubmitting = Boolean(createAccount?.isSubmitting);
  const errorMessage = isEmailEditedSinceSubmit ? undefined : createAccount?.errorMessage;

  useEffect(() => {
    if (successMessage) successRef.current?.focus();
  }, [successMessage]);

  const handleCreateAccount = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsEmailEditedSinceSubmit(false);
    createAccount?.onSubmit(email);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setIsEmailEditedSinceSubmit(true);
  };

  return (
    <PageSection>
      <Header>
        <PageHeading title={heading} subtitle={subheading} centered splitLines />
        {confirmationEmail && (
          <ConfirmationMeta>
            {labels.sentConfirmationPrefix}
            <strong>{confirmationEmail}</strong>
          </ConfirmationMeta>
        )}
        {confirmationNumber && (
          <ConfirmationMeta>
            {labels.confirmationNumberPrefix}
            <strong>{confirmationNumber}</strong>
          </ConfirmationMeta>
        )}
        {summaryLines.map((line, index) => (
          <ConfirmationMeta key={`summary-${index}`}>{line}</ConfirmationMeta>
        ))}
        {voucherCodes.length > 0 && (
          <VoucherList>
            {voucherCodes.map((code, index) => (
              <VoucherItem key={`voucher-${index}`}>{code}</VoucherItem>
            ))}
          </VoucherList>
        )}
      </Header>

      <Columns>
        <BookingColumn>
          <SectionTitle>{labels.bookingSectionTitle}</SectionTitle>
          <BookingList>
            {bookingItems.map((item) => (
              <BookingSummaryRow
                key={item.id}
                {...item}
                noImageLabel={labels.noImage}
                newTabLabel={labels.newTab}
              />
            ))}
          </BookingList>
        </BookingColumn>

        {createAccount && (
          <ManageBooking>
            <SectionTitle>{labels.manageBookingTitle}</SectionTitle>
            <ManageBookingDescription>{labels.manageBookingDescription}</ManageBookingDescription>
            {successMessage ? (
              <CreateAccountSuccess ref={successRef} role="status" tabIndex={-1}>
                {successMessage}
              </CreateAccountSuccess>
            ) : (
              <CreateAccountForm onSubmit={handleCreateAccount}>
                <Input
                  type="email"
                  value={email}
                  placeholder={labels.emailLabel}
                  aria-label={labels.emailLabel}
                  autoComplete="email"
                  required
                  error={Boolean(errorMessage)}
                  aria-describedby={errorMessage ? createAccountErrorId : undefined}
                  onChange={handleEmailChange}
                />
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  aria-busy={isSubmitting || undefined}
                >
                  {isSubmitting && <Spinner aria-hidden />}
                  {labels.createAccountCta}
                </Button>
                {errorMessage && (
                  <ErrorText id={createAccountErrorId} role="alert">
                    {errorMessage}
                  </ErrorText>
                )}
                {errorMessage && createAccount.errorAction && (
                  <CreateAccountErrorAction>
                    <TextButton type="button" size="sm" onClick={createAccount.errorAction.onClick}>
                      {createAccount.errorAction.label}
                    </TextButton>
                  </CreateAccountErrorAction>
                )}
              </CreateAccountForm>
            )}
          </ManageBooking>
        )}
      </Columns>

      {upsell && upsell.items.length > 0 && (
        <UpsellSection>
          <PageHeading title={labels.upsellHeading} subtitle={labels.upsellSubheading} centered />
          <UpsellGridWrapper>
            <CardGrid $columns={3} $mobileColumns={2}>
              {upsell.items.map((item) => (
                <ItemCard
                  key={item.id}
                  image={{
                    src: item.image?.src,
                    alt: item.image?.alt ?? item.title,
                    placeholderLabel: labels.noImage,
                  }}
                  imageAspectRatio="16 / 9"
                  title={item.title}
                  meta={item.description}
                  price={item.price}
                  action={{ label: labels.upsellAddCta, onClick: () => upsell.onAdd(item.id) }}
                />
              ))}
            </CardGrid>
          </UpsellGridWrapper>
        </UpsellSection>
      )}
    </PageSection>
  );
}
